import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, delay, filter, map } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { globalBookingFlights, selectGlobalBookingState } from '@fcom/common/store/selectors/global-booking.selectors';
import { AppConstants } from '@fcom/booking/app-constants';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { BookingAndAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { Step } from '@fcom/common/interfaces/step.interface';
import { currentOfferSelection, currentPassengers } from '@fcom/booking/store/selectors/booking-cart-state.selector';
import { finShare } from '@fcom/rx';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { IconButtonTheme, IconButtonSize } from '@fcom/ui-components';

import {
  BookingSummaryBound,
  BookingSummaryPassenger,
  BookingSummaryService,
  SummaryTripDetails,
} from '../../interfaces/booking-summary.interface';

@Component({
  selector: 'fin-booking-summary',
  styleUrls: ['./booking-summary.component.scss'],
  templateUrl: './booking-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BookingSummaryComponent {
  private store$ = inject<Store<BookingAndAppState>>(Store);

  readonly C: typeof AppConstants = AppConstants;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconButtonSize = IconButtonSize;

  readonly close = output<void>();
  readonly edit = output<Step>();

  readonly currentStep = input<Step>();
  readonly unpaidServices = input<BookingSummaryService[]>([]);
  readonly onlyShowAncillaries = input(false);
  readonly flowContext = input<string>();
  readonly hideChangeButton = input(false);

  STEPS: typeof BOOKING_STEPS = BOOKING_STEPS;

  readonly currentStep$: Observable<Step | undefined> = toObservable(this.currentStep);
  readonly unpaidServices$: Observable<BookingSummaryService[]> = toObservable(this.unpaidServices);
  readonly hasUnpaidServices$: Observable<boolean> = this.unpaidServices$.pipe(map((services) => services.length > 0));

  tripDetails$: Observable<SummaryTripDetails>;
  travelers$: Observable<BookingSummaryPassenger[]>;
  isOneWay$: Observable<boolean>;
  currentOffer$: Observable<BookingSummaryBound[]>;
  ctaOpenDelayPassed$: Observable<boolean>;

  constructor() {
    this.tripDetails$ = this.store$.select(selectGlobalBookingState).pipe(
      map((common) => ({
        ...common,
        travelClass: common.travelClasses.selected,
      }))
    );

    this.currentOffer$ = combineLatest([
      this.store$.pipe(currentOfferSelection()),
      this.store$.pipe(globalBookingFlights()),
    ]).pipe(
      filter(([offers]) => offers && Boolean(offers.length)),
      map(([offers, flights]) => {
        return offers.map((offer, index) => {
          offer.departure.cityName = flights[index]?.origin?.cityName;
          offer.arrival.cityName = flights[index]?.destination?.cityName;

          return offer;
        });
      })
    );

    // Passenger details from cart
    this.travelers$ = this.store$.pipe(currentPassengers(), finShare());
    // Total
    this.ctaOpenDelayPassed$ = of(true).pipe(delay(350)); // Open (height) transition + 100ms
    this.isOneWay$ = this.tripDetails$.pipe(map((details) => details.tripType === TripType.ONEWAY));
  }
}
